// Initializes the Dash0 Web SDK for browser telemetry: page views, web
// vitals, error capture (uncaught errors and unhandled rejections; handled
// ones via reportError), and fetch instrumentation with W3C traceparent
// propagation to same-origin requests (SSR + API routes) and, via
// `propagators` below, to the cross-origin product API.
// https://www.dash0.com/documentation/dash0/get-started/websites
//
// Next 14 has no instrumentation-client.ts convention (that arrived in
// Next 15.3), so both routers call initDash0() explicitly: at module scope of
// src/pages/_app.tsx (pages router) and via <Dash0Init /> in the root layout
// (app router). The window guard and init-once flag make both calls safe
// during SSR and across router boundaries.

import { init } from '@dash0/sdk-web';

import { SERVICE_NAME } from '@src/constants/observability';

import { resolveDash0Dataset } from './dash0-dataset';
import { seedDash0IdentityFromProfileCache } from './dash0-identity';

const vercelEnv = process.env.NEXT_PUBLIC_VERCEL_ENV ?? 'development';
const dash0Dataset = resolveDash0Dataset(vercelEnv);

const escapeRegExp = (value: string) => value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

// Matches the URL prefix only up to a path/query/fragment boundary, so a
// lookalike host (`https://api.example.com.evil.tld`) never receives the
// traceparent header.
const urlPrefixPattern = (prefix: string) => new RegExp(`^${escapeRegExp(prefix)}(?:[/?#]|$)`);

let initialized = false;

export function initDash0() {
  // Client components also render on the server; the SDK is browser-only.
  if (typeof window === 'undefined' || initialized) {
    return;
  }
  initialized = true;

  // The token is ingest-only and scoped in Dash0's org settings; without it
  // (local dev) the SDK stays off entirely.
  if (!process.env.NEXT_PUBLIC_DASH0_ENDPOINT_URL || !process.env.NEXT_PUBLIC_DASH0_AUTH_TOKEN) {
    return;
  }

  // Seed identity before init(): identify()/addSignalAttribute() only mutate
  // SDK module state, which init() merges rather than resets, and init()
  // records the initial page-view synchronously — a seed placed after init()
  // leaves that first signal anonymous. Ownership rules live with the seed.
  seedDash0IdentityFromProfileCache();

  // The SDK injects traceparent into same-origin requests only by default;
  // browser calls to the product API (App Router check-in fetches and the
  // axios services) are cross-origin, so without this entry their server-side
  // traces in numa-product would never join the browser trace. The API
  // Gateway's CORS preflight allows all request headers (`*`), so adding the
  // header is safe.
  const productApiUrl = process.env.NEXT_PUBLIC_PRODUCT_API_URL;

  init({
    // The SDK's fetch instrumentation re-wraps responses via `new Response(...)`,
    // which drops `Response.url`. Next's App Router relies on `res.url` when a
    // client navigation targets a Pages Router route (it falls back to a full
    // document navigation at that URL) — with an empty url it navigates to `/`,
    // which the middleware turns into `/trips`. Exempt Next's RSC navigation
    // fetches (marked by the `_rsc` query param) from instrumentation entirely.
    ignoreUrls: [/[?&]_rsc=/],
    // Matches the service.name on the server spans registered by dash0.server.config.ts
    serviceName: SERVICE_NAME,
    serviceVersion: process.env.NEXT_PUBLIC_VERCEL_GIT_COMMIT_SHA ?? 'local',
    // Matches deployment.environment.name on the server spans
    environment: vercelEnv,
    endpoint: {
      url: process.env.NEXT_PUBLIC_DASH0_ENDPOINT_URL,
      authToken: process.env.NEXT_PUBLIC_DASH0_AUTH_TOKEN,
      dataset: dash0Dataset,
    },
    ...(productApiUrl && {
      propagators: [{ type: 'traceparent' as const, match: [urlPrefixPattern(productApiUrl)] }],
    }),
    // No error filtering (ignoreErrorMessages): all browser errors are
    // ingested unfiltered so we can observe the real noise profile in Dash0
    // before deciding which patterns deserve filters.
    // sessionSamplingRate defaults to 100%, which is right at current traffic levels.
  });
}
